// @ts-nocheck
// interactions — Wave 1 daily-UX components
// Toast, SwipeableRow, PullToRefresh, DateTimePickerSheet, Checkbox, BulkBar
// Ported verbatim from the design bundle (pilotools-crm/project/interactions.jsx).
import React from 'react';
import { Icon, Button } from './ui';
import { t } from './i18n';
import './runtime';

const { useState: useStateI, useEffect: useEffectI, useRef: useRefI, useCallback: useCallbackI, useContext: useContextI } = React;

// ── Haptic helper (Telegram WebApp if present) ──────────────────────────
function haptic(kind = 'impact', style = 'medium') {
  try {
    const h = window.Telegram?.WebApp?.HapticFeedback;
    if (!h) return;
    if (kind === 'notification') h.notificationOccurred(style);
    else h.impactOccurred(style);
  } catch (e) {}
}

// ── Toast system ────────────────────────────────────────────────────────
const ToastCtx = React.createContext(() => {});
function useToast() { return useContextI(ToastCtx); }

function ToastProvider({ children }) {
  const [toasts, setToasts] = useStateI([]);
  const timers = useRefI({});

  const dismiss = useCallbackI((id) => {
    setToasts((ts) => ts.filter((x) => x.id !== id));
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const show = useCallbackI((msg, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    // Keep up to 5 in state so the "+N" group count is accurate as they expire.
    setToasts((ts) => [...ts.slice(-4), { id, msg, undo: opts.undo }]);
    timers.current[id] = setTimeout(() => dismiss(id), opts.duration || 3200);
    return id;
  }, [dismiss]);

  useEffectI(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  // M-7 §3.8 toast queue: a single toast clutters less than a tower. With 3+
  // stacked, show only the latest individually and collapse the rest into a
  // "… и ещё N" chip rather than rendering all of them.
  const shown = toasts.length >= 3 ? toasts.slice(-1) : toasts;
  const extra = toasts.length - shown.length;

  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toast-host">
        {shown.map((tt) => (
          <div className="toast" key={tt.id}>
            <Icon.Check size={16} sw={2.5} style={{ color: 'var(--brand-300)', flexShrink: 0 }} />
            <span className="toast-msg">{tt.msg}</span>
            {tt.undo && (
              <button className="toast-undo" onClick={() => { tt.undo(); dismiss(tt.id); }}>{t('toast.undo')}</button>
            )}
            <button className="toast-x" onClick={() => dismiss(tt.id)}><Icon.X size={14} sw={2.5} /></button>
          </div>
        ))}
        {extra > 0 && (
          <div className="toast" key="toast-more" style={{ justifyContent: 'center' }}>
            <span className="toast-msg" style={{ color: 'var(--text-3)' }}>{t('toast.more', { n: extra })}</span>
          </div>
        )}
      </div>
    </ToastCtx.Provider>
  );
}

// ── SwipeableRow ─────────────────────────────────────────────────────────
function SwipeableRow({ onRight, onLeft, rightLabel, leftLabel, disabled, children }) {
  const [dx, setDx] = useStateI(0);
  const [snap, setSnap] = useStateI(false);
  const st = useRefI(null);
  const moved = useRefI(false);
  const TH = 70;

  const down = (e) => {
    if (disabled) return;
    st.current = { x: e.clientX, y: e.clientY, lock: null };
    moved.current = false;
    setSnap(false);
  };
  const move = (e) => {
    if (!st.current) return;
    const dX = e.clientX - st.current.x, dY = e.clientY - st.current.y;
    if (st.current.lock == null && (Math.abs(dX) > 6 || Math.abs(dY) > 6)) {
      st.current.lock = Math.abs(dX) > Math.abs(dY) ? 'x' : 'y';
    }
    if (st.current.lock === 'x') {
      moved.current = true;
      let v = Math.max(-130, Math.min(130, dX));
      if ((v > 0 && !onRight) || (v < 0 && !onLeft)) v = v * 0.25; // resist if no action
      setDx(v);
    }
  };
  const up = () => {
    if (!st.current) return;
    const d = dx;
    setSnap(true);
    if (d > TH && onRight) { haptic('notification', 'success'); setDx(150); setTimeout(() => { onRight(); setDx(0); }, 200); }
    else if (d < -TH && onLeft) { haptic('impact', 'medium'); setDx(-150); setTimeout(() => { onLeft(); setDx(0); }, 200); }
    else setDx(0);
    st.current = null;
  };
  const onClickCap = (e) => { if (moved.current) { e.stopPropagation(); e.preventDefault(); moved.current = false; } };

  return (
    <div className="swipe-wrap"
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}>
      {onRight && (
        <div className="swipe-action swipe-action-right" style={{ opacity: dx > 4 ? 1 : 0 }}>
          <Icon.Check size={18} sw={2.75} />{dx > TH ? (rightLabel || t('swipe.done')) : ''}
        </div>
      )}
      {onLeft && (
        <div className="swipe-action swipe-action-left" style={{ opacity: dx < -4 ? 1 : 0 }}>
          {dx < -TH ? (leftLabel || t('swipe.snooze')) : ''}<Icon.Clock size={18} sw={2.5} />
        </div>
      )}
      <div className="swipe-fg" onClickCapture={onClickCap}
        style={{ transform: `translateX(${dx}px)`, transition: snap ? 'transform 0.22s cubic-bezier(.2,.8,.3,1)' : 'none' }}>
        {children}
      </div>
    </div>
  );
}

// ── PullToRefresh (acts as the scroll container) ─────────────────────────
function PullToRefresh({ onRefresh, className, style, children }) {
  const [pull, setPull] = useStateI(0);
  const [state, setState] = useStateI('idle'); // idle | pull | ready | refreshing
  const sc = useRefI(null);
  const sy = useRefI(null);

  const down = (e) => { sy.current = (sc.current && sc.current.scrollTop <= 0) ? e.clientY : null; };
  const move = (e) => {
    if (sy.current == null || state === 'refreshing') return;
    const dy = e.clientY - sy.current;
    if (dy > 0 && sc.current.scrollTop <= 0) {
      const p = Math.min(dy * 0.5, 80);
      setPull(p); setState(p >= 54 ? 'ready' : 'pull');
    } else if (dy < -4) { sy.current = null; setPull(0); setState('idle'); }
  };
  const up = () => {
    if (sy.current == null) return;
    if (state === 'ready') {
      setState('refreshing'); setPull(44); haptic('impact', 'light');
      Promise.resolve(onRefresh && onRefresh()).then(() => { setState('idle'); setPull(0); });
    } else { setPull(0); setState('idle'); }
    sy.current = null;
  };

  const label = state === 'refreshing' ? t('ptr.refreshing') : state === 'ready' ? t('ptr.release') : t('ptr.pull');

  return (
    <div ref={sc} className={className}
      style={{ ...style, position: 'relative' }}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      <div className="ptr-indicator" style={{ height: 0, transform: `translateY(${Math.max(pull - 22, state === 'refreshing' ? 12 : -22)}px)`, opacity: pull > 6 || state === 'refreshing' ? 1 : 0 }}>
        <Icon.Refresh size={14} sw={2.25} className={state === 'refreshing' ? 'ptr-spin' : ''} style={{ color: state === 'ready' ? 'var(--brand-500)' : 'var(--text-3)', transform: state === 'refreshing' ? 'none' : `rotate(${pull * 3}deg)` }} />
        {label}
      </div>
      <div style={{ transform: pull ? `translateY(${pull}px)` : 'none', transition: sy.current ? 'none' : 'transform 0.25s cubic-bezier(.2,.8,.3,1)' }}>
        {children}
      </div>
    </div>
  );
}

// ── Work-hours aware date/time picker ────────────────────────────────────
function buildTimeSlots() {
  const W = window.WORK_HOURS || { start: '10:00', end: '17:00', lunchStart: '13:00', lunchEnd: '13:30' };
  const toMin = (s) => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
  const start = toMin(W.start), end = toMin(W.end), ls = toMin(W.lunchStart), le = toMin(W.lunchEnd);
  const slots = [];
  for (let m = start; m < end; m += 30) {
    const lunch = m >= ls && m < le;
    const hh = String(Math.floor(m / 60)).padStart(2, '0'), mm = String(m % 60).padStart(2, '0');
    slots.push({ min: m, label: `${hh}:${mm}`, lunch });
  }
  return slots;
}
function buildDays(n = 14) {
  const wd = (t('cal.weekdays') || 'Пн,Вт,Ср,Чт,Пт,Сб,Вс').split(',');
  const mo = (t('cal.months') || 'янв,фев,мар,апр,май,июн,июл,авг,сен,окт,ноя,дек').split(',');
  const base = new Date(2026, 4, 30); // demo "today" = 30 May 2026 (Sat)
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(base); d.setDate(base.getDate() + i);
    const dow = (d.getDay() + 6) % 7; // Mon=0
    if (dow === 6) continue; // skip Sunday (Pn–Sb)
    let title = `${wd[dow]}, ${d.getDate()} ${mo[d.getMonth()]}`;
    if (i === 0) title = t('dp.today');
    else if (i === 1) title = t('dp.tomorrow');
    out.push({ key: i, title, full: `${d.getDate()} ${mo[d.getMonth()]}` });
  }
  return out;
}

function DateTimePickerSheet({ open, onClose, onConfirm }) {
  const days = open ? buildDays() : [];
  const slots = open ? buildTimeSlots() : [];
  const [day, setDay] = useStateI(0);
  const [slot, setSlot] = useStateI(null);
  if (!open) return null;

  const pickNearest = () => { setDay(0); const first = slots.find((s) => !s.lunch); setSlot(first ? first.min : null); };
  const dayObj = days.find((d) => d.key === day) || days[0];
  const slotObj = slots.find((s) => s.min === slot);
  const valid = dayObj && slotObj;
  const resultLabel = valid ? `${day === 0 ? t('dp.today') : day === 1 ? t('dp.tomorrow') : dayObj.full} ${slotObj.label}` : '';

  return (
    <div className="sheet-backdrop" onClick={onClose} style={{ zIndex: 40 }}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="row between middle">
          <div className="t-h1">{t('dp.title')}</div>
          <button className="btn btn-ghost btn-sm" onClick={pickNearest}>
            <Icon.Sparkle size={13} sw={2.5} />{t('dp.nearest')}
          </button>
        </div>
        <div className="picker-cols" style={{ marginTop: 10 }}>
          <div className="picker-col">
            {days.map((d) => (
              <div key={d.key} className={`picker-item ${day === d.key ? 'is-sel' : ''}`} onClick={() => setDay(d.key)}>
                <span>{d.title}</span>
                {day === d.key && <Icon.Check size={15} sw={2.75} />}
              </div>
            ))}
          </div>
          <div className="picker-col">
            {slots.map((s) => s.lunch
              ? <div key={s.min} className="picker-lunch">{s.label} · {t('dp.lunch')}</div>
              : <div key={s.min} className={`picker-item ${slot === s.min ? 'is-sel' : ''}`} onClick={() => setSlot(s.min)}>
                  <span>{s.label}</span>
                  {slot === s.min && <Icon.Check size={15} sw={2.75} />}
                </div>
            )}
          </div>
        </div>
        <div className="row gap-2" style={{ marginTop: 12 }}>
          <Button variant="ghost" full onClick={onClose}>{t('btn.cancel')}</Button>
          <Button variant="primary" full onClick={() => onConfirm(resultLabel)}
            style={{ opacity: valid ? 1 : 0.4, pointerEvents: valid ? 'auto' : 'none' }}>
            {t('dp.apply')}{valid ? ` · ${resultLabel}` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Checkbox + BulkBar ────────────────────────────────────────────────────
function Checkbox({ checked, onChange }) {
  return (
    <span className={`checkbox ${checked ? 'is-on' : ''}`} onClick={(e) => { e.stopPropagation(); onChange(!checked); }}>
      {checked && <Icon.Check size={14} sw={3} />}
    </span>
  );
}

export {
  haptic, ToastProvider, useToast, SwipeableRow, PullToRefresh,
  DateTimePickerSheet, Checkbox, buildTimeSlots, buildDays,
};
